/*
 * Copyright (C) 2023 - present Marek Kuzora - All Rights Reserved.
 */


import { Client } from 'fierry/flux';

import ShapeEnum, { getShapeToCount } from 'shared/enum/shape';

import Galaxy from 'shared/query/galaxy';
import Navigation from 'shared/client/navigation';

import * as Coords from 'shared/geo/coords';
import * as Shapes from 'shared/geo/shapes';


export default new Client([ Navigation, Galaxy ], class
{
  constructor (navigation, galaxy)
  {
    this.galaxy = galaxy;

    this.source = navigation.getCurrent();
    this.target = navigation.getRawTarget();
  }


  onDone ()
  {
    const shape = this.__getTargetShape__();

    this.galaxy.attackUnit(this.source, this.target, shape);
  }


  getSelectionTargets ()
  {
    if (this.target.getSpaceship())
    {
      return [ this.target ];
    }

    const mapCallback = (coords) =>
    {
      return this.galaxy.getLocation(coords);
    };

    return this.__getTargetShape__().data.map(mapCallback);
  }


  __getTargetShape__ ()
  {
    const targetShape = this.__getConstantsShape__(this.target.getCoords(), this.source.getCoords())[0];

    if (this.target.getSpaceship())
    {
      return targetShape;
    }

    const downgradeCallback = (coords) =>
    {
      return this.galaxy.getLocation(coords).getZone() == null;
    };

    return Shapes.getDowngrade(this.target.getCoords(), targetShape, downgradeCallback);
  }


  __getConstantsShape__ (target, source)
  {
    const type = this.getUnitConstants().getTargetShape();

    return this.getUnitConstants().isRangeAttack() ? Shapes.getMaxVariants(target, source, type) : Shapes.getMinVariants(target, source, type)
  }


  getError ()
  {
    if (this.source.equals(this.target))
    {
      return 'Unit cannot attack zone it occupies.';
    }

    const distance  = Coords.getDistance(this.source.getCoords(), this.target.getCoords());

    if (distance < this.getUnitConstants().getTargetMinRange())
    {
      return 'Target zone is too close. Please select more distant target.';
    }

    if (distance > this.getUnitConstants().getTargetMaxRange())
    {
      return 'Target zone is out of range. Please select a closer target.';
    }

    const targetSpaceship = this.target.getSpaceship();

    if (targetSpaceship)
    {
      if (this.source.getUser().getID() === targetSpaceship.getUserID())
      {
        return 'Cannot attack your own spaceship.';
      }

      if (targetSpaceship.getStatus().isProduction())
      {
        return 'Cannot attack spaceship under construction.';
      }

      if (targetSpaceship.getStatus().isDock())
      {
        return 'Cannot attack docked spaceship.';
      }
    }

    if (!this.target.getZone() && !this.target.getSpaceship())
    {
      return 'Cannot attack empty space.';
    }

    return null;
  }


  getInitial ()
  {
    if (this.target == null)
    {
      return 'Select target zone';
    }

    return null;
  }


  getUnitConstants ()
  {
    const unit = this.source.getZone().getUnit();

    return this.galaxy.getConstants().getUnit(unit.getType());
  }


});
